import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import PageErrorBoundary from './PageErrorBoundary';
import SectionErrorBoundary from './SectionErrorBoundary';

/**
 * Higher-order component (HOC) that wraps a component with the appropriate error boundary
 *
 * @param {React.Component} Component - The component to wrap
 * @param {Object} options - Configuration options
 * @param {string} options.type - Type of error boundary: 'app', 'page', or 'section'
 * @param {string} options.name - Name of the component (for error messages)
 * @param {Function} options.onError - Error handler function
 * @param {Function} options.onRetry - Custom retry handler
 * @param {React.ReactNode} options.fallback - Custom fallback UI
 * @returns {React.Component} Wrapped component with error boundary
 */
export const withErrorBoundary = (Component, options = {}) => {
  const { type = 'section', name, onError, onRetry, fallback, ...rest } = options;

  // Return a new component
  return function WithErrorBoundary(props) {
    // Choose the appropriate error boundary based on type
    switch (type) {
      case 'app':
        return (
          <ErrorBoundary onError={onError}>
            <Component {...props} />
          </ErrorBoundary>
        );
      case 'page':
        return (
          <PageErrorBoundary
            pageName={name || Component.displayName || Component.name}
            onError={onError}
            onRetry={onRetry}
            fallback={fallback}
            {...rest}
          >
            <Component {...props} />
          </PageErrorBoundary>
        );
      case 'section':
      default:
        return (
          <SectionErrorBoundary
            sectionName={name || Component.displayName || Component.name}
            onError={onError}
            onRetry={onRetry}
            fallback={fallback}
            {...rest}
          >
            <Component {...props} />
          </SectionErrorBoundary>
        );
    }
  };
};

/**
 * Component version of the error boundary wrapper
 * Use this when you need to wrap JSX directly rather than a component definition
 */
export function ErrorBoundaryWrapper({
  children,
  type = 'section',
  name,
  onError,
  onRetry,
  fallback,
  ...rest
}) {
  switch (type) {
    case 'app':
      return <ErrorBoundary onError={onError}>{children}</ErrorBoundary>;
    case 'page':
      return (
        <PageErrorBoundary
          pageName={name}
          onError={onError}
          onRetry={onRetry}
          fallback={fallback}
          {...rest}
        >
          {children}
        </PageErrorBoundary>
      );
    case 'section':
    default:
      return (
        <SectionErrorBoundary
          sectionName={name}
          onError={onError}
          onRetry={onRetry}
          fallback={fallback}
          {...rest}
        >
          {children}
        </SectionErrorBoundary>
      );
  }
}

export default withErrorBoundary;
